import {
  BookOpen, CloudCog, Code2, CreditCard, Database, Image, Library,
  Monitor, Package, Palette, PenTool, Presentation, Share2, Smartphone,
  Sparkles,
} from 'lucide-react'

export const company = {
  name: 'HI RIGHT Corporate Services',
  short: 'HI RIGHT',
  email: 'hello@your-domain.com',
  phone: '+00 00000 00000',
  address: 'Business address to be confirmed',
}

export const stats = [
  ['14', 'Creative and technology services'],
  ['360°', 'Design-to-development capability'],
  ['24/7', 'Digital infrastructure support'],
  ['100%', 'Requirement-led execution'],
]

export const services = [
  {
    slug: 'logo-designing', title: 'Logo Designing', icon: Palette,
    short: 'Distinctive, memorable logos that give your business a strong visual identity.',
    body: 'We create original logo concepts shaped by your brand personality, audience and industry, then prepare practical files for print and digital use.',
    outcomes: ['Original logo concepts', 'Colour and typography direction', 'Print and digital file formats'], accent: '#E8BF5A',
  },
  {
    slug: 'social-media-posts', title: 'Social Media Posts', icon: Share2,
    short: 'On-brand social media creatives designed to inform, engage and promote.',
    body: 'We design platform-ready social media posts that communicate clearly and maintain a consistent look across your content calendar.',
    outcomes: ['Platform-ready post designs', 'Consistent visual templates', 'Campaign and promotional creatives'], accent: '#73D6C6',
  },
  {
    slug: 'brochures-and-flyers', title: 'Brochures and Flyers', icon: BookOpen,
    short: 'Clear, attractive marketing materials that present your message professionally.',
    body: 'We organise your information into polished brochures and flyers that are easy to understand and ready for print or digital distribution.',
    outcomes: ['Single and multi-page layouts', 'Print-ready artwork', 'Digital sharing formats'], accent: '#B99CFF',
  },
  {
    slug: 'posters-and-banners', title: 'Posters and Banners', icon: Image,
    short: 'High-impact promotional graphics for print, display and digital campaigns.',
    body: 'We design posters and banners with strong visual hierarchy so your key message is noticed quickly in physical and online spaces.',
    outcomes: ['Promotional poster layouts', 'Print and web banner sizes', 'Campaign-ready artwork'], accent: '#FF957D',
  },
  {
    slug: 'business-cards', title: 'Business Cards', icon: CreditCard,
    short: 'Professional business cards that make every introduction feel credible.',
    body: 'We create clean, brand-aligned business card layouts with careful typography, contact hierarchy and production-ready specifications.',
    outcomes: ['Front and back design', 'Print-ready files', 'Brand-consistent typography'], accent: '#68A7FF',
  },
  {
    slug: 'website-graphics', title: 'Website Graphics', icon: Monitor,
    short: 'Custom visual assets that make websites clearer, stronger and more engaging.',
    body: 'We design web banners, hero graphics, icons and supporting imagery optimised for responsive layouts and fast digital delivery.',
    outcomes: ['Hero and section graphics', 'Web banners and icons', 'Responsive export sizes'], accent: '#F2C96D',
  },
  {
    slug: 'packaging-design', title: 'Packaging Design', icon: Package,
    short: 'Practical, eye-catching packaging that helps products stand out.',
    body: 'We combine brand identity, product information and production requirements to create packaging that is attractive and ready for manufacture.',
    outcomes: ['Packaging concepts', 'Label and dieline artwork', 'Production-ready files'], accent: '#73D6C6',
  },
  {
    slug: 'magazines-books-catalogues', title: 'Magazines, Books and Catalogues', icon: Library,
    short: 'Structured, readable publication design for long-form print and digital content.',
    body: 'We design covers and interior layouts with consistent grids, typography and imagery for magazines, books and product catalogues.',
    outcomes: ['Cover and interior design', 'Multi-page layout systems', 'Print and digital editions'], accent: '#B99CFF',
  },
  {
    slug: 'presentation-slides', title: 'Presentation Slides', icon: Presentation,
    short: 'Polished slide decks that communicate ideas with clarity and confidence.',
    body: 'We turn your content into well-structured presentations with consistent layouts, purposeful visuals and an easy-to-follow narrative.',
    outcomes: ['Custom slide templates', 'Pitch and business decks', 'Charts and visual storytelling'], accent: '#FF957D',
  },
  {
    slug: 'web-development', title: 'Web Development', icon: Code2,
    short: 'Responsive, reliable websites built around your business requirements.',
    body: 'We develop maintainable websites and web applications with responsive interfaces, secure integrations and dependable performance.',
    outcomes: ['Responsive development', 'API and CMS integrations', 'Testing and deployment'], accent: '#68A7FF',
  },
  {
    slug: 'app-development', title: 'App Development', icon: Smartphone,
    short: 'User-friendly mobile and business applications designed for real workflows.',
    body: 'We design and develop applications from requirements through testing, with scalable architecture and intuitive user experiences.',
    outcomes: ['Application planning', 'Frontend and backend development', 'Testing and release support'], accent: '#F2C96D',
  },
  {
    slug: 'database-management', title: 'Database Management', icon: Database,
    short: 'Secure, organised database solutions that keep business information dependable.',
    body: 'We design, maintain and optimise databases with attention to availability, access control, backup and application performance.',
    outcomes: ['Database design and setup', 'Performance and maintenance', 'Backup and access management'], accent: '#73D6C6',
  },
  {
    slug: 'ui-ux-designing', title: 'UI/UX Designing', icon: PenTool,
    short: 'Clear, intuitive interfaces shaped around user needs and business goals.',
    body: 'We plan user journeys, wireframes and interface systems that make websites and applications easier to understand and use.',
    outcomes: ['User flows and wireframes', 'Interface design systems', 'Interactive prototypes'], accent: '#B99CFF',
  },
  {
    slug: 'aws-management', title: 'AWS Management', icon: CloudCog,
    short: 'Reliable AWS setup, monitoring and optimisation for cloud-based systems.',
    body: 'We help configure and manage AWS environments with practical controls for security, performance, availability and cost.',
    outcomes: ['Cloud setup and deployment', 'Monitoring and maintenance', 'Security and cost optimisation'], accent: '#E8BF5A',
  },
]

export const growthPillars = [
  ['Discover', 'We clarify your requirements, audience and practical priorities.'],
  ['Design', 'Visual concepts and user experiences turn ideas into clear solutions.'],
  ['Develop', 'Web, app and database engineering brings approved ideas to life.'],
  ['Deploy', 'Careful testing and cloud delivery prepare the work for real use.'],
  ['Support', 'Ongoing management keeps digital systems reliable and current.'],
]

export const technologies = {
  'Design': ['Figma', 'Adobe Photoshop', 'Adobe Illustrator', 'Adobe InDesign'],
  'Web development': ['React', 'JavaScript', 'WordPress', 'Node.js'],
  'App development': ['React Native', 'Flutter', 'Android', 'iOS'],
  'Databases': ['MySQL', 'PostgreSQL', 'MongoDB', 'SQL Server'],
  'Cloud': ['AWS EC2', 'Amazon S3', 'Amazon RDS', 'AWS CloudWatch'],
  'Delivery': ['Git', 'GitHub', 'Docker', 'CI/CD'],
}

export const industries = ['Startups', 'Retail', 'Healthcare', 'Education', 'Hospitality', 'Real estate', 'Finance', 'Manufacturing', 'E-commerce', 'Professional services']

export const faqs = [
  ['Which service should we start with?', 'Share your immediate requirement and intended outcome. We will help identify whether you need a focused design deliverable, a digital product or ongoing technical management.'],
  ['Can you follow our existing brand guidelines?', 'Yes. We can work within an established identity or create a new visual direction when required.'],
  ['Do you supply print-ready design files?', 'Yes. Brochures, flyers, posters, banners, business cards, packaging and publications can be prepared to the specifications supplied by your printer.'],
  ['Can you redesign or develop our existing website?', 'Yes. We can improve the interface, user experience and underlying web implementation based on the current system and your goals.'],
  ['Do you develop mobile applications?', 'Yes. We can plan, design, develop and test applications based on the required platforms, features and integrations.'],
  ['Can you manage an existing database?', 'Yes. We can assess its structure, security, backups and performance before recommending ongoing management work.'],
  ['What does AWS management include?', 'The scope can include deployment, monitoring, access controls, backups, reliability checks and cost optimisation.'],
  ['Can you combine design and development services?', 'Yes. UI/UX, website graphics, web development, app development, databases and AWS management can be delivered as one coordinated project.'],
]

export const posts = [
  { slug: 'consistent-visual-identity', cat: 'Graphic design', title: 'Why a consistent visual identity builds trust', excerpt: 'How logos, colours, typography and everyday design materials work together.', time: '7 min read' },
  { slug: 'ui-ux-foundations', cat: 'UI/UX design', title: 'What makes a digital interface easier to use', excerpt: 'A practical look at hierarchy, navigation, feedback and accessibility.', time: '6 min read' },
  { slug: 'reliable-cloud-foundations', cat: 'Cloud management', title: 'The foundations of a reliable AWS environment', excerpt: 'How monitoring, access control, backups and cost visibility support digital products.', time: '5 min read' },
]

export const serviceIcons = { Sparkles }
